import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit3,
  Maximize2,
  Play,
  Eye,
  Save,
  Share2,
  Presentation } from
'lucide-react';
import SlideRenderer from './SlideRenderer';
import FullscreenPresentationView from './FullscreenPresentationView';

interface ProcessedSlide {
  id: string;
  title: string;
  content: string[];
  layoutType: string;
  processedContent: any;
  designApplied: boolean;
  type?: 'title' | 'content' | 'image' | 'list' | 'conclusion' | 'quote' | 'comparison' | 'image-text';
  notes?: string;
  imageUrl?: string;
  imageAlt?: string;
}

interface Template {
  id: string;
  name: string;
  preview?: {
    titleSlide: {
      backgroundColor: string;
      textColor: string;
      accentColor: string;
    };
    contentSlide: {
      backgroundColor: string;
      textColor: string;
      accentColor: string;
    };
    fonts: {
      heading: string;
      body: string;
    };
  };
  colorScheme: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
}

interface PresentationViewProps {
  slides: ProcessedSlide[];
  template: Template;
  title: string;
  onEdit?: () => void;
  onDownload?: () => void;
  onSave?: () => void;
  onClose?: () => void;
  onExportPDF?: () => void;
}

const PresentationView: React.FC<PresentationViewProps> = ({
  slides,
  template,
  title,
  onEdit,
  onDownload,
  onSave,
  onClose,
  onExportPDF
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const currentSlide = slides[currentSlideIndex];

  const nextSlide = () => {
    if (currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlideIndex(index);
  };

  const startFullscreenPresentation = () => {
    setIsFullscreen(true);
  };

  const closeFullscreenPresentation = () => {
    setIsFullscreen(false);
  };

  const savePresentation = async () => {
    try {
      if (onSave) {
        await onSave();
      }
      console.log('Saving presentation:', { slides, template, title });
    } catch (error) {
      console.error('Failed to save presentation:', error);
    }
  };

  // Ensure template has proper structure for backward compatibility
  const normalizedTemplate = {
    ...template,
    preview: template.preview || {
      titleSlide: {
        backgroundColor: template.colorScheme?.background || '#ffffff',
        textColor: template.colorScheme?.primary || '#1e293b',
        accentColor: template.colorScheme?.accent || '#3b82f6'
      },
      contentSlide: {
        backgroundColor: template.colorScheme?.background || '#ffffff',
        textColor: template.colorScheme?.primary || '#1e293b',
        accentColor: template.colorScheme?.accent || '#3b82f6'
      },
      fonts: {
        heading: 'Inter, system-ui, sans-serif',
        body: 'Inter, system-ui, sans-serif'
      }
    }
  };

  const renderSlideContent = (slide: ProcessedSlide) => {
    const { processedContent } = slide;
    const colors = normalizedTemplate.colorScheme || normalizedTemplate.preview?.contentSlide;

    // Use the enhanced SlideRenderer with proper template application
    if (processedContent && processedContent.layout) {
      switch (processedContent.layout) {
        case 'Hero Section':
          return (
            <div
              className="h-full flex flex-col justify-center items-center text-center p-12"
              style={{ backgroundColor: colors?.background || normalizedTemplate.preview.titleSlide.backgroundColor }} data-id="mgncwfjqa" data-path="src/components/ai/PresentationView.tsx">

              <h1
                className="text-6xl font-bold mb-6"
                style={{ color: colors?.primary || normalizedTemplate.preview.titleSlide.textColor }} data-id="m43263upd" data-path="src/components/ai/PresentationView.tsx">

                {processedContent.title}
              </h1>
              <p
                className="text-2xl mb-8 max-w-3xl"
                style={{ color: colors?.secondary || normalizedTemplate.preview.titleSlide.textColor }} data-id="bkne3b16u" data-path="src/components/ai/PresentationView.tsx">

                {processedContent.subtitle}
              </p>
              <Button
                size="lg"
                style={{
                  backgroundColor: colors?.accent || normalizedTemplate.preview.titleSlide.accentColor,
                  borderColor: colors?.accent || normalizedTemplate.preview.titleSlide.accentColor
                }} data-id="ippdfj1al" data-path="src/components/ai/PresentationView.tsx">

                {processedContent.ctaText}
              </Button>
            </div>);


        case 'Feature Grid':
          return (
            <div
              className="h-full p-12"
              style={{ backgroundColor: colors?.background || normalizedTemplate.preview.contentSlide.backgroundColor }} data-id="9lgwwc2mk" data-path="src/components/ai/PresentationView.tsx">

              <h2
                className="text-4xl font-bold mb-12 text-center"
                style={{ color: colors?.primary || normalizedTemplate.preview.contentSlide.textColor }} data-id="5hu2d00t2" data-path="src/components/ai/PresentationView.tsx">

                {processedContent.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="mi5dyyr3y" data-path="src/components/ai/PresentationView.tsx">
                {processedContent.features?.map((feature: any, index: number) =>
                <div key={index} className="text-center" data-id="3b316jl5u" data-path="src/components/ai/PresentationView.tsx">
                    <div
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: colors?.accent || normalizedTemplate.preview.contentSlide.accentColor }} data-id="3oabi3n7x" data-path="src/components/ai/PresentationView.tsx">

                      <span className="text-white text-2xl font-bold" data-id="mjfi0yzep" data-path="src/components/ai/PresentationView.tsx">{index + 1}</span>
                    </div>
                    <h3
                    className="text-xl font-semibold mb-3"
                    style={{ color: colors?.primary || normalizedTemplate.preview.contentSlide.textColor }} data-id="4obxdj4qx" data-path="src/components/ai/PresentationView.tsx">

                      {feature.title}
                    </h3>
                    <p style={{ color: colors?.secondary || normalizedTemplate.preview.contentSlide.textColor }} data-id="pj43c2xy5" data-path="src/components/ai/PresentationView.tsx">
                      {feature.description}
                    </p>
                  </div>
                )}
              </div>
            </div>);


        default:
          // Use SlideRenderer for other types
          const normalizedSlide = {
            id: slide.id,
            title: slide.title,
            content: Array.isArray(slide.content) ? slide.content.join('\n') : slide.content?.join('\n') || '',
            type: slide.type || 'content',
            notes: slide.notes,
            imageUrl: slide.imageUrl,
            imageAlt: slide.imageAlt,
            template: normalizedTemplate
          };

          return (
            <SlideRenderer
              slide={normalizedSlide}
              template={normalizedTemplate}
              isEditing={false} data-id="15o2kroqv" data-path="src/components/ai/PresentationView.tsx" />);


      }
    }

    // Fallback to basic rendering
    return (
      <div
        className="h-full p-12"
        style={{ backgroundColor: colors?.background || normalizedTemplate.preview.contentSlide.backgroundColor }} data-id="vw058znzl" data-path="src/components/ai/PresentationView.tsx">

        <h2
          className="text-4xl font-bold mb-8"
          style={{ color: colors?.primary || normalizedTemplate.preview.contentSlide.textColor }} data-id="nf2slyw04" data-path="src/components/ai/PresentationView.tsx">

          {slide.title}
        </h2>
        <ul className="space-y-6 text-xl" data-id="ik691ljf9" data-path="src/components/ai/PresentationView.tsx">
          {slide.content.map((point, index) =>
          <li key={index} className="flex items-start gap-4" data-id="a88yd5kgj" data-path="src/components/ai/PresentationView.tsx">
              <span
              className="w-3 h-3 rounded-full mt-3"
              style={{ backgroundColor: colors?.accent || normalizedTemplate.preview.contentSlide.accentColor }} data-id="oymp1s6t0" data-path="src/components/ai/PresentationView.tsx" />

              <span style={{ color: colors?.secondary || normalizedTemplate.preview.contentSlide.textColor }} data-id="7vee0jcws" data-path="src/components/ai/PresentationView.tsx">
                {point}
              </span>
            </li>
          )}
        </ul>
      </div>);

  };

  // Fullscreen presentation mode
  if (isFullscreen) {
    return (
      <FullscreenPresentationView
        slides={slides}
        template={normalizedTemplate}
        title={title}
        onClose={closeFullscreenPresentation}
        autoplay={false} data-id="y8wxh89pn" data-path="src/components/ai/PresentationView.tsx" />);


  }

  return (
    <div className="max-w-7xl mx-auto p-6" data-id="avmxdgkkl" data-path="src/components/ai/PresentationView.tsx">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="imob6vicv" data-path="src/components/ai/PresentationView.tsx">
        <div data-id="ipwnxg298" data-path="src/components/ai/PresentationView.tsx">
          <h1 className="text-3xl font-bold mb-2" data-id="ffw0uedbr" data-path="src/components/ai/PresentationView.tsx">{title}</h1>
          <div className="flex gap-2" data-id="whlu251t5" data-path="src/components/ai/PresentationView.tsx">
            <Badge variant="outline" data-id="ai3n8c8ob" data-path="src/components/ai/PresentationView.tsx">{template.name}</Badge>
            <Badge variant="outline" data-id="af4iwv6a3" data-path="src/components/ai/PresentationView.tsx">{slides.length} slides</Badge>
          </div>
        </div>
        <div className="flex gap-3" data-id="kzkpk97ra" data-path="src/components/ai/PresentationView.tsx">
          <Button onClick={savePresentation} variant="outline" data-id="bdr2gi12x" data-path="src/components/ai/PresentationView.tsx">
            <Save className="h-4 w-4 mr-2" data-id="a7j14y6vd" data-path="src/components/ai/PresentationView.tsx" />
            Save
          </Button>
          {onEdit &&
          <Button onClick={onEdit} variant="outline" data-id="re43sv096" data-path="src/components/ai/PresentationView.tsx">
              <Edit3 className="h-4 w-4 mr-2" data-id="wu9n15yj7" data-path="src/components/ai/PresentationView.tsx" />
              Edit Content
            </Button>
          }
          {(onDownload || onExportPDF) &&
          <Button onClick={onDownload || onExportPDF} variant="outline" data-id="w5b011cdo" data-path="src/components/ai/PresentationView.tsx">
              <Download className="h-4 w-4 mr-2" data-id="rjg8e5zcv" data-path="src/components/ai/PresentationView.tsx" />
              Export
            </Button>
          }
          <Button data-id="m2mxw23nb" data-path="src/components/ai/PresentationView.tsx">
            <Share2 className="h-4 w-4 mr-2" data-id="8gwirha4r" data-path="src/components/ai/PresentationView.tsx" />
            Share
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="s1t4y4pbt" data-path="src/components/ai/PresentationView.tsx">
        {/* Slide Navigation */}
        <div className="lg:col-span-1" data-id="ov14y9xf5" data-path="src/components/ai/PresentationView.tsx">
          <Card data-id="1scsyssas" data-path="src/components/ai/PresentationView.tsx">
            <CardContent className="p-4" data-id="f6s9tbnqh" data-path="src/components/ai/PresentationView.tsx">
              <h3 className="font-semibold mb-4" data-id="wgkgmuffm" data-path="src/components/ai/PresentationView.tsx">Slides</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto" data-id="qxhrs0dnu" data-path="src/components/ai/PresentationView.tsx">
                {slides.map((slide, index) =>
                <button
                  key={slide.id}
                  onClick={() => goToSlide(index)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  currentSlideIndex === index ?
                  'bg-blue-50 border-blue-200' :
                  'hover:bg-gray-50 border-gray-200'}`
                  } data-id="6gy1e9mlv" data-path="src/components/ai/PresentationView.tsx">

                    <div className="flex items-center gap-3" data-id="6bta5cjmp" data-path="src/components/ai/PresentationView.tsx">
                      <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-sm font-medium" data-id="0i6f67ari" data-path="src/components/ai/PresentationView.tsx">
                        {index + 1}
                      </div>
                      <div className="flex-grow min-w-0" data-id="5vyn1bt1q" data-path="src/components/ai/PresentationView.tsx">
                        <p className="font-medium text-sm truncate" data-id="9uonjcki3" data-path="src/components/ai/PresentationView.tsx">{slide.title}</p>
                        <p className="text-xs text-gray-500" data-id="cq09xafv0" data-path="src/components/ai/PresentationView.tsx">{slide.layoutType}</p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Slide Display */}
        <div className="lg:col-span-3" data-id="4cjk0xc2u" data-path="src/components/ai/PresentationView.tsx">
          <Card className="relative" data-id="ruch9cj3x" data-path="src/components/ai/PresentationView.tsx">
            <CardContent className="p-0" data-id="pfwupayoj" data-path="src/components/ai/PresentationView.tsx">
              {/* Slide Content */}
              <div className="aspect-video bg-white rounded-lg overflow-hidden border" data-id="bvhyx0a0c" data-path="src/components/ai/PresentationView.tsx">
                {renderSlideContent(currentSlide)}
              </div>

              {/* Slide Controls */}
              <div className="flex items-center justify-between p-4 border-t" data-id="b2sfnpnyn" data-path="src/components/ai/PresentationView.tsx">
                <div className="flex items-center gap-3" data-id="xbk6f9l9f" data-path="src/components/ai/PresentationView.tsx">
                  <Button
                    onClick={prevSlide}
                    disabled={currentSlideIndex === 0}
                    variant="outline"
                    size="sm" data-id="eyo1xy72y" data-path="src/components/ai/PresentationView.tsx">

                    <ChevronLeft className="h-4 w-4" data-id="9incevcp6" data-path="src/components/ai/PresentationView.tsx" />
                  </Button>
                  <span className="text-sm text-gray-600" data-id="yplu5r223" data-path="src/components/ai/PresentationView.tsx">
                    {currentSlideIndex + 1} of {slides.length}
                  </span>
                  <Button
                    onClick={nextSlide}
                    disabled={currentSlideIndex === slides.length - 1}
                    variant="outline"
                    size="sm" data-id="88kh7rc87" data-path="src/components/ai/PresentationView.tsx">

                    <ChevronRight className="h-4 w-4" data-id="uwdv0uipx" data-path="src/components/ai/PresentationView.tsx" />
                  </Button>
                </div>

                <div className="flex gap-2" data-id="77n6w682g" data-path="src/components/ai/PresentationView.tsx">
                  <Button variant="outline" size="sm" onClick={startFullscreenPresentation} data-id="15nrgyv3n" data-path="src/components/ai/PresentationView.tsx">
                    <Play className="h-4 w-4 mr-2" data-id="l8850yf30" data-path="src/components/ai/PresentationView.tsx" />
                    Slideshow
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={startFullscreenPresentation}
                    title="Present in Fullscreen" data-id="rojyk8r6h" data-path="src/components/ai/PresentationView.tsx">

                    <Maximize2 className="h-4 w-4" data-id="eldbe1q5p" data-path="src/components/ai/PresentationView.tsx" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Slide Details */}
          <Card className="mt-4" data-id="gl6wp7j4e" data-path="src/components/ai/PresentationView.tsx">
            <CardContent className="p-4" data-id="vxc3lsyya" data-path="src/components/ai/PresentationView.tsx">
              <div className="flex items-center gap-2 mb-3" data-id="7ijo6yc35" data-path="src/components/ai/PresentationView.tsx">
                <Eye className="h-4 w-4 text-gray-500" data-id="nql1gwbn9" data-path="src/components/ai/PresentationView.tsx" />
                <span className="font-medium" data-id="xeg1jbfo6" data-path="src/components/ai/PresentationView.tsx">Slide Details</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-id="hq49022ga" data-path="src/components/ai/PresentationView.tsx">
                <div data-id="ial6hoe5t" data-path="src/components/ai/PresentationView.tsx">
                  <span className="text-gray-500" data-id="zkb0uzi5e" data-path="src/components/ai/PresentationView.tsx">Layout:</span>
                  <p className="font-medium" data-id="jkapa7e2t" data-path="src/components/ai/PresentationView.tsx">{currentSlide.layoutType}</p>
                </div>
                <div data-id="uyvre0ff8" data-path="src/components/ai/PresentationView.tsx">
                  <span className="text-gray-500" data-id="5tlx38gs9" data-path="src/components/ai/PresentationView.tsx">Content Points:</span>
                  <p className="font-medium" data-id="bfsa1olf1" data-path="src/components/ai/PresentationView.tsx">{currentSlide.content.length}</p>
                </div>
                <div data-id="nco6dt4rz" data-path="src/components/ai/PresentationView.tsx">
                  <span className="text-gray-500" data-id="tmhk1eh9r" data-path="src/components/ai/PresentationView.tsx">Template:</span>
                  <p className="font-medium" data-id="alkvte4t4" data-path="src/components/ai/PresentationView.tsx">{template.name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>);

};

export default PresentationView;